import { useCallback, useEffect, useRef, useState } from 'react';
import { GrabDots } from './GrabDots';

/**
 * A draggable pane width that remembers where you put it.
 *
 * The transcript is where the room's argument lives — sometimes you want it wide
 * enough to actually read a Skeptic's three-paragraph objection, and sometimes you
 * want it out of the way so the board can breathe. Which one is true changes minute
 * to minute, so it shouldn't be a decision we made for you at build time.
 *
 * `handleOn` says which EDGE THE HANDLE SITS ON — not which side the pane is, which is
 * the reading that has already cost one inverted drag. The direction depends on it: a
 * handle on the LEFT of a right-hand pane grows the pane as you drag left, and a handle
 * on the TOP of a bottom pane grows it as you drag up.
 *
 * `top`/`bottom` resize HEIGHT. `width` is still what comes back — it is the pane's
 * extent along whichever axis it was given, and a second name for one number would be
 * one more thing to keep true.
 */
/**
 * The handle edge for a pane on a given side.
 *
 * `handleOn` asks which edge the HANDLE sits on. Everybody reads it as which
 * side the PANE is, gets the opposite, and ships an inverted drag — twice in
 * this codebase now, in two different workspaces.
 *
 * So say where the pane is and let this answer the question that keeps being
 * got wrong. A resizable pane's handle is always on the edge facing the rest
 * of the layout, which is the opposite side from the one it is anchored to.
 */
export function handleEdgeForPaneOn(
  side: 'left' | 'right' | 'top' | 'bottom',
): 'left' | 'right' | 'top' | 'bottom' {
  return { left: 'right', right: 'left', top: 'bottom', bottom: 'top' }[side] as
    | 'left'
    | 'right'
    | 'top'
    | 'bottom';
}

export function useResizablePane({
  storageKey,
  initial,
  min = 240,
  max = 800,
  handleOn = 'left',
  collapsedSize,
  persistCollapsed = false,
}: {
  storageKey: string;
  initial: number;
  min?: number;
  max?: number;
  /** Which edge the drag handle sits on. NOT which side the pane is. */
  handleOn?: 'left' | 'right' | 'top' | 'bottom';
  /** Set this and a CLICK on the handle (as opposed to a drag) folds the pane down to
   *  this size, and a second click puts it back where it was.
   *
   *  It is deliberately allowed to sit below `min`: the floor exists so a DRAG cannot
   *  leave you with a pane too small to use by accident, and a deliberate click is not
   *  an accident. Collapsed is meant to be smaller than anything you could drag to. */
  collapsedSize?: number;
  /** Does a FOLD outlive the page, the way a dragged size does?
   *
   *  Off by default, and the default is the interesting case — see the note in the
   *  initialiser. Turn it on for a pane whose fold reads as "I don't work with this
   *  panel open", rather than "not for the next few minutes": there, coming back to find
   *  it open again is the surprise, and re-folding it every visit is a chore. */
  persistCollapsed?: boolean;
}) {
  const [width, setWidth] = useState<number>(() => {
    // BY DEFAULT A COLLAPSED PANE DOES NOT SURVIVE. A stored value below `min` — which
    // is what folding writes — falls through to `initial`, on purpose: folding is "get
    // out of my way while I read this", not a standing preference about how the screen
    // is laid out. Arriving to find half of it already hidden, by a decision made
    // somewhere else days ago, is the opposite of seeing what you came to see.
    //
    // `persistCollapsed` says this pane's fold is the other kind — a standing "I don't
    // work with this open", where reopening on its own is the surprise. Only the exact
    // `collapsedSize` is honoured, not anything under `min`: the point is to restore a
    // FOLD, and a stray sub-floor number in storage is corruption, not a preference.
    //
    // A DRAGGED size persists either way. That one is always a preference: you sized the
    // pane to read it, and you meant it.
    const saved = Number(localStorage.getItem(storageKey));
    if (saved >= min && saved <= max) return saved;
    if (persistCollapsed && collapsedSize != null && saved === collapsedSize) {
      return collapsedSize;
    }
    return initial;
  });
  const [dragging, setDragging] = useState(false);
  const startPos = useRef(0);
  const startWidth = useRef(0);
  // Did this pointer interaction MOVE? A press that never travels is a click, and a
  // click means something different from a drag. Without the distinction the toggle
  // would fire at the end of every resize.
  const moved = useRef(false);
  // Where to go back to. Seeded from `initial` so the first-ever expand still lands
  // somewhere sensible rather than at zero.
  const restore = useRef(initial);
  const vertical = handleOn === 'top' || handleOn === 'bottom';
  // The two edges where dragging TOWARD the origin grows the pane.
  const inverted = handleOn === 'left' || handleOn === 'top';

  // FOLD OR UNFOLD FROM ANYWHERE, not just the handle. The handle is a hairline: fine
  // to grab when you can see the pane it belongs to, a poor target for bringing back a
  // pane that is currently a 36px strip — and that strip is the obvious thing to click.
  // Same restore memory either way, so it makes no difference which one you use.
  const toggle = useCallback(() => {
    if (collapsedSize == null) return;
    setWidth(w => {
      if (w <= collapsedSize) return restore.current;
      restore.current = w;
      return collapsedSize;
    });
  }, [collapsedSize]);

  // POINTER events, not mouse. A touch drag never produces mousedown/mousemove — the
  // browser synthesises a click after the gesture ends, and nothing before it — so a
  // mouse-only handle simply cannot be grabbed on a phone, or in Chrome's device
  // emulation, which dispatches touch. Pointer events cover mouse, touch and pen in one
  // set, and `touch-action: none` on the handle (see ResizeHandle) stops the browser
  // panning the page instead of giving us the drag.
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault(); // or the browser starts a text selection mid-drag
      // Capture, so the drag survives the pointer outrunning a handle a few pixels wide.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      startPos.current = vertical ? e.clientY : e.clientX;
      startWidth.current = width;
      moved.current = false;
      setDragging(true);
    },
    [width, vertical],
  );

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: PointerEvent) => {
      const delta = (vertical ? e.clientY : e.clientX) - startPos.current;
      // A few pixels of slack: a press always wobbles a little, and a click that
      // registered as a drag would silently do nothing.
      if (Math.abs(delta) > 3) moved.current = true;
      if (!moved.current) return;
      const next = inverted ? startWidth.current - delta : startWidth.current + delta;
      // DRAG IT SHUT. The floor used to be absolute, so a pane could only be closed by
      // knowing that a CLICK on the handle did it — and a pane you are already dragging
      // hard toward the edge is a pane you are plainly trying to close. Pulling further
      // and watching nothing happen is the control refusing the obvious reading of the
      // gesture.
      //
      // NOT AT THE FLOOR, though. The floor earns its keep: it stops a resize leaving
      // you with something too narrow to read by accident, and that is still worth
      // having. So the snap sits halfway from the floor down to the folded size —
      // closing takes a deliberate pull well past the point where the pane visibly
      // stops shrinking, which is not a thing anyone does by mistake.
      //
      // Live, and reversible mid-gesture: pull back out and it reopens and follows the
      // pointer again, so nothing is committed until you let go.
      if (collapsedSize != null && next < (collapsedSize + min) / 2) {
        // Where an unfold should land. Guarded, or dragging a pane that is ALREADY
        // folded would overwrite the size you meant to come back to with the fold.
        if (startWidth.current > collapsedSize) restore.current = startWidth.current;
        setWidth(collapsedSize);
        return;
      }
      setWidth(Math.min(max, Math.max(min, next)));
    };
    const onUp = () => {
      setDragging(false);
      // A CLICK, not a drag: fold the pane away, or put it back where it was. Two
      // useful sizes, one gesture, and the handle is already the thing your pointer is
      // on when you decide you want the board rather than the transcript.
      if (!moved.current) toggle();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    // A touch drag that leaves the viewport, or an OS gesture stealing the pointer,
    // fires `pointercancel` and never `pointerup` — without this the pane would stay
    // stuck in dragging state and follow the cursor around afterwards.
    window.addEventListener('pointercancel', onUp);
    // While dragging, kill selection + keep the resize cursor even when the pointer
    // outruns the 4px handle — otherwise a fast drag flickers back to a text caret.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = vertical ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging, min, max, vertical, inverted, toggle, collapsedSize]);

  useEffect(() => {
    localStorage.setItem(storageKey, String(width));
  }, [storageKey, width]);

  // `setWidth` is exposed so a caller can offer a reset (double-click the handle) —
  // it goes through the same state, so it persists like any drag.
  return {
    width,
    dragging,
    onPointerDown,
    setWidth,
    toggle,
    collapsed: collapsedSize != null && width <= collapsedSize,
  };
}

/**
 * THE CLICKABLE BAND AROUND A DIVIDER — one width, both axes.
 *
 * The two handles had drifted apart because each grew its hit area as an offset from
 * whatever it happened to LOOK like rather than from what it is for. The horizontal
 * divider is a 10px bar and added 8px either side, giving 26; the vertical one is a 1px
 * hairline and added 4, giving 9. Same gesture, same hand, three times the target on one
 * axis. Both were wrong in the direction of their own thickness.
 *
 * 16 is the middle of the two, and it is stated ONCE as a size instead of twice as an
 * offset — so the next person to tune how thick a divider looks cannot move how big it
 * is to hit.
 */
const HANDLE_HIT_PX = 16;

/** The grab strip. Thin, but with a generous invisible hit area — a 1px target is a
 *  cruel thing to ask someone to hit. `className`/`style` are for placement only: in a
 *  grid the handle is its own column, and it has to be told to fill the row's height. */
export function ResizeHandle({
  onPointerDown,
  onDoubleClick,
  dragging,
  className = '',
  style,
  title = 'Drag to resize',
  axis = 'x',
  grabber = false,
}: {
  onPointerDown: (e: React.PointerEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  dragging: boolean;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  /** 'x' divides two columns, 'y' two stacked rows. */
  axis?: 'x' | 'y';
  /** Draw the six-dot grip in the middle. A hairline is discoverable enough beside a
   *  mouse cursor that changes shape over it; on a touch screen there is no cursor to
   *  change, so the affordance has to be something you can see. */
  grabber?: boolean;
}) {
  const y = axis === 'y';
  return (
    <div
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      style={style}
      // touch-none: without it the browser claims the gesture for panning and the drag
      // never reaches us. `select-none` for the same reason `userSelect` is killed
      // during the drag — a slow press on a divider should not start selecting text.
      //
      // A REAL BAR when it is horizontal, not a hairline. The vertical divider sits
      // between two columns where a 1px line plus an invisible hit strip is fine — the
      // cursor changes shape over it and tells you it is there. A horizontal divider
      // between stacked panes has no such tell on a touch screen, so it gets height you
      // can see and aim at.
      className={`group relative shrink-0 touch-none select-none ${
        y
          ? 'h-2 w-full cursor-row-resize border-y border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800'
          : 'w-px cursor-col-resize'
      } ${className}`}
      title={title}
    >
      {/* WHAT YOU CAN GRAB. Invisible, always — it paints nothing at any size.
          This and the tell below were briefly ONE box, on the theory that a highlight
          wider than the live area is a control lying about its edges. The theory is
          fine and the conclusion was backwards: it made the PAINT follow the hit area
          instead of the hit area follow the paint, so grabbing the column divider lit a
          sixteen-pixel indigo band down the full height of the screen. A generous
          target is a thing you feel; it is not a thing you should be able to see. */}
      <div
        data-testid="resize-hit"
        className={`absolute z-10 ${
          y
            ? 'inset-x-0 top-1/2 -translate-y-1/2'
            : 'inset-y-0 left-1/2 -translate-x-1/2'
        }`}
        style={y ? { height: HANDLE_HIT_PX } : { width: HANDLE_HIT_PX }}
      />
      {/* WHAT SAYS SO. Tracks the DIVIDER's own thickness, not the hit area's: the
          horizontal bar tints where it already is, and the column divider stays the
          hairline it is — three pixels is enough to read as "live" down a full-height
          rule and little enough to still look like a rule. */}
      <div
        data-testid="resize-tell"
        className={`pointer-events-none absolute z-0 transition-colors ${
          y ? 'inset-0' : 'inset-y-0 left-1/2 w-[3px] -translate-x-1/2'
        } ${
          dragging
            ? 'bg-indigo-400'
            : 'bg-transparent group-hover:bg-indigo-300/60 dark:group-hover:bg-indigo-500/40'
        }`}
      />
      {grabber && (
        /* THE SAME MARK THE RUN BAR'S FOLD BUTTON WEARS — see GrabDots, which
           exists so the two cannot drift apart again. Turned to face the axis:
           three dots along the divider by two across it, so a column divider
           gets a 2×3 and a row divider a 3×2. */
        <GrabDots
          columns={y ? 3 : 2}
          className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
          dotClassName={
            dragging
              ? 'bg-indigo-500'
              : 'bg-gray-400 group-hover:bg-indigo-400 dark:bg-gray-500'
          }
        />
      )}
    </div>
  );
}
